import {Button, CopyText, Icon} from '@therms/atalaya'
import {
  IdLabel,
  ImageSmall,
  LocationActiveServicePeriods,
  LocationGroupTags,
  LocationName,
  makeAdminLocationURL,
  RPCDocs,
  useCurrentUserAnPCheck,
  useStickyStack,
} from '@therms/react-modules'
import {MdOpenInNew} from 'react-icons/md'
import {formatAddress} from '@therms/web-js'
import {LocationActivitySummary} from './LocationActivitySummary'
import RoleElement = RPCDocs.Auth.GetIdentityInfo.v1.Roles

interface LocationLeftSideBarProps {
  activitySummary?: RPCDocs.Locations.GetLocationActivitySummary.v1.ActivitySummary
  location: RPCDocs.Locations.GetLocationProfile.v1.Location
  locationId: string
}

export function LocationSidebarDetails({
  activitySummary,
  location,
  locationId,
}: LocationLeftSideBarProps) {
  const stickyStackRef = useStickyStack()

  const hasAdminAccess = useCurrentUserAnPCheck(({anp}) =>
    anp.roles.includes(RoleElement.Admin),
  )

  const appliedServicePeriods = location.servicePeriods?.applied ?? 0

  const hasActivity =
    !!activitySummary &&
    (!!activitySummary.onSiteUsers?.length ||
      !!activitySummary.nearbyUsers?.length ||
      !!activitySummary.lastActivityLogEntry ||
      !!activitySummary.lastOnSiteUser)

  return (
    <div ref={stickyStackRef} className="p-sm bg-inherit">
      {/* Inactive location banner */}
      {!location.active && (
        <div className="bg-critical-faded text-color-critical border-critical p-xs mb-sm rounded border text-center font-semibold">
          Inactive Location
        </div>
      )}

      {!!location.primaryImageUrl && (
        <ImageSmall
          className="h-32 w-full"
          src={location.primaryImageUrl}
          lightbox
          rounded
        />
      )}

      <div data-sticky className="mt-xxs sticky bg-inherit">
        {!!location.id && (
          <IdLabel
            className="text-color-subtle text-xs font-bold"
            idLabel={location.idLabel}
          />
        )}

        {location.parentLocationId && (
          <div className="text-bold text-color-subtle text-sm font-light">
            {' '}
            <LocationName locationId={location.parentLocationId} />
          </div>
        )}

        <div className="text-md text-color-strong font-medium">
          {location.name}
        </div>

        {location.address && (
          <CopyText alwaysShow value={formatAddress(location.address)}>
            <span className="text-sm font-light">
              {formatAddress(location.address, false)}
            </span>
          </CopyText>
        )}
      </div>

      {hasAdminAccess && (
        <Button
          className="mt-xs w-full"
          outline
          size="xs"
          subtle
          variant="neutral"
        >
          <a
            href={makeAdminLocationURL(locationId)}
            target="_blank"
            rel="noreferrer noopener nofollow"
          >
            <div className="gap-x-xxs p-xxs flex items-center">
              <span>Open in ADMIN</span>
              <Icon Icon={MdOpenInNew} size="xs" />
            </div>
          </a>
        </Button>
      )}

      <div className="gap-xs mt-xs flex flex-wrap">
        <LocationGroupTags locationGroupIds={location.locationGroupIds} />
      </div>

      {hasActivity && (
        <div data-sticky className="py-sm sticky bg-inherit shadow">
          <div className="bg-border h-px" />
        </div>
      )}

      {location.settings.modules?.servicePeriods &&
        appliedServicePeriods > 0 && (
          <div className="mt-sm mb-base">
            <LocationActiveServicePeriods
              applied={appliedServicePeriods}
              locationId={locationId}
            />
          </div>
        )}

      {hasActivity && (
        <LocationActivitySummary activitySummary={activitySummary} />
      )}
    </div>
  )
}
